<script setup>
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import {
  BarChart2,
  Bell,
  Clock,
  Users,
  Briefcase,
  Monitor,
  Activity,
  FileText,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  User
} from 'lucide-vue-next'
import FenixLogo from '@/components/FenixLogo.vue'
import fenixLogoHorizontal from '@/assets/fenix-logo-horizontal.png'
import { useAuth } from "@/hooks/useAuth"
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { useI18n } from 'vue-i18n'

const { user, logout } = useAuth()
const route = useRoute()
const { t } = useI18n()

const isActiveItem = item => {
  if (route.path === item.path) return true
  // "Mi Portal" links to /app/employee/{id}/detail — only match detail paths
  if (item.path.includes('/detail/') && route.path.includes('/detail/')) {
    return item.path.split('/detail/')[0] === route.path.split('/detail/')[0]
  }
  // "Empleados" links to /app/employee/active — match list views but not detail views
  if (item.path.startsWith('/app/employee/') && !item.path.includes('/detail/')) {
    return route.path.startsWith('/app/employee/') && !route.path.includes('/detail/')
  }
  if (item.path.includes('/activities') && route.path.startsWith('/activities')) return true
  if (item.path.startsWith('/app/settings') && route.path.startsWith('/app/settings')) return true
  if (item.path === '/reports' && route.path.startsWith('/reports')) return true
  if (item.path === '/alerts' && route.path.startsWith('/alerts')) return true
  return false
}

// Persistir estado del sidebar en localStorage
const SIDEBAR_STATE_KEY = 'sidebar-collapsed'
const isCollapsed = ref(localStorage.getItem(SIDEBAR_STATE_KEY) === 'true')

const toggleSidebar = () => {
  isCollapsed.value = !isCollapsed.value
  localStorage.setItem(SIDEBAR_STATE_KEY, isCollapsed.value.toString())
}

const isAdmin = computed(() => {
  if (!user.value) return false
  const primary = user.value.role || null
  const roles = Array.isArray(user.value.roles) ? user.value.roles : []
  return primary === 'admin' || roles.includes('admin')
})

const isManager = computed(() => {
  if (!user.value) return false
  const primary = user.value.role || null
  const roles = Array.isArray(user.value.roles) ? user.value.roles : []
  return primary === 'manager' || roles.includes('manager')
})

// Employee-only: has employee role but no admin/manager role
const isEmployeeOnly = computed(() => {
  if (!user.value) return false
  const roles = Array.isArray(user.value.roles) ? user.value.roles : []
  return roles.includes('employee') && !roles.includes('admin') && !roles.includes('manager')
})

// Has an employee record (for showing "Mi Portal" in sidebar)
const hasEmployeeRecord = computed(() => !!user.value?.employee_id)

const userPermissions = computed(() => user.value?.permissions || [])

const hasPermission = (permission) => {
  if (isAdmin.value) return true
  return userPermissions.value.includes(permission)
}

const hasAnyPermission = (permissions) => {
  if (isAdmin.value) return true
  return permissions.some(p => userPermissions.value.includes(p))
}

const menuItems = computed(() => {
  // Employee-only users see only their own portal
  if (isEmployeeOnly.value) {
    const empId = user.value?.employee_id
    if (empId) {
      return [
        { label: t('sidebar.my_portal'), icon: User, path: `/app/employee/${empId}/detail/utilization` }
      ]
    }
    return []
  }

  // For admins, show all items. For managers, filter by permissions.
  const allItems = [
    { label: t('sidebar.trends'), icon: BarChart2, path: '/trends', permission: 'trends.view' },
    { label: t('sidebar.realtime'), icon: Clock, path: '/realtime', permission: 'realtime.view' },
    { label: t('sidebar.employees'), icon: Users, path: '/app/employee/active', permission: 'employees.view' },
    { label: t('sidebar.teams'), icon: Briefcase, path: '/app/teams', permission: 'teams.view' },
    { label: t('sidebar.screens'), icon: Monitor, path: '/screens', permission: 'screens.view' },
    { label: t('sidebar.activities'), icon: Activity, path: '/activities/timeline', permission: 'activities.view' },
    { label: t('sidebar.alerts'), icon: Bell, path: '/alerts', permission: 'alerts.view' },
    { label: t('sidebar.reports'), icon: FileText, path: '/reports', permission: 'reports.view' },
  ]

  const items = allItems.filter(item => hasPermission(item.permission))

  // Admin/manager with employee record: add "Mi Portal" link
  if (hasEmployeeRecord.value) {
    const empId = user.value.employee_id
    items.push({ label: t('sidebar.my_portal'), icon: User, path: `/app/employee/${empId}/detail/utilization` })
  }

  // Settings: visible if admin or manager with any settings-related permission
  const settingsPerms = [
    'settings.general', 'settings.users', 'settings.utilization', 'settings.locations', 'settings.organization',
    'tracking.view', 'tracking.edit', 'users.view', 'users.edit', 'apps.view', 'apps.edit'
  ]
  if (hasAnyPermission(settingsPerms)) {
    items.push({ label: t('sidebar.settings'), icon: Settings, path: '/app/settings' })
  }

  return items
})
</script>

<template>
  <aside 
    class="relative flex flex-col bg-card/50 backdrop-blur-xl border-r border-border/50 transition-all duration-300 ease-in-out h-screen sticky top-0 z-40"
    :class="[isCollapsed ? 'w-20' : 'w-64']"
  >
    <!-- Border Toggle Area (Clickable Line) -->
    <div 
      class="absolute -right-1.5 top-0 bottom-0 w-3 cursor-col-resize z-50 group flex items-center justify-center"
      @click="toggleSidebar"
      title="Click para expandir/colapsar"
    >
        <!-- Visual Line -->
        <div class="h-full w-[1px] bg-border/0 group-hover:bg-primary/50 transition-colors delay-75"></div>
    </div>

    <!-- Header Logo -->
    <div class="h-20 flex items-center px-4 py-4 border-b border-border/30" :class="isCollapsed ? 'justify-center' : 'justify-start'">
       <img v-if="!isCollapsed" :src="fenixLogoHorizontal" alt="Fenix Guardian" class="w-full h-full object-contain transition-all duration-300" />
       <FenixLogo v-else :size="'sm'" />
    </div>

    <!-- Navigation -->
    <nav class="flex-1 overflow-y-auto py-6 flex flex-col gap-1 px-3 scrollbar-thin scrollbar-thumb-border/50 hover:scrollbar-thumb-border/80">
       <router-link 
         v-for="item in menuItems" 
         :key="item.path" 
         :to="item.path"
         class="flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 group relative"
         :class="[
            isActiveItem(item)
             ? 'bg-primary/10 text-primary font-medium shadow-sm' 
             : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
            isCollapsed ? 'justify-center' : ''
         ]"
       >
         <component :is="item.icon" class="w-5 h-5 shrink-0 transition-colors" />
         
         <span 
            class="whitespace-nowrap overflow-hidden transition-all duration-300 origin-left"
            :class="[isCollapsed ? 'w-0 opacity-0 absolute' : 'w-auto opacity-100']"
         >
            {{ item.label }}
         </span>
       </router-link>
    </nav>

    <!-- User Footer -->
    <div class="border-t border-border/30 p-4 bg-card/30">
        <router-link 
            to="/app/settings/profile"
            class="flex items-center gap-3 p-2 rounded-lg hover:bg-accent/50 transition-colors cursor-pointer overflow-hidden group"
        >
            <!-- Avatar Placeholder -->
            <div class="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-primary-foreground font-bold shrink-0 shadow-md">
                {{ user?.full_name?.charAt(0).toUpperCase() || user?.name?.charAt(0).toUpperCase() || 'U' }}
            </div>
            
            <div class="flex-1 min-w-0 transition-all duration-300" :class="[isCollapsed ? 'w-0 opacity-0 hidden' : 'w-auto opacity-100 block']">
                <p class="text-sm font-medium truncate text-foreground">{{ user?.full_name || user?.name || 'Usuario' }}</p>
                <p class="text-xs text-muted-foreground truncate group-hover:text-primary transition-colors">{{ t('sidebar.view_profile') }}</p>
            </div>
        </router-link>
        
        <button 
            @click="logout" 
            class="mt-3 w-full flex items-center gap-3 px-2 py-2 text-destructive/80 hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors text-sm"
             :class="{'justify-center': isCollapsed}"
             :title="t('sidebar.logout')"
        >
            <LogOut class="w-5 h-5" />
            <span :class="[isCollapsed ? 'hidden' : 'block']">{{ t('sidebar.logout') }}</span>
        </button>
    </div>

  </aside>
</template>
